import { DailyEventObjectParticipant } from '@daily-co/daily-js';
import { useDailyEvent } from '@daily-co/daily-react';
import { datadogLogs } from '@datadog/browser-logs';
import { useQueryClient } from '@tanstack/react-query';
import { callLogContext } from '@web-app/utils/daily-client/call-log-context';
import { isDialOutParticipant } from '@web-app/utils/daily-client/dial-out-participant';
import { useRouter } from 'next/router';

import { QUERY_APP_USER_STATUS_KEY } from './consts';

/**
 * Hook for tracking when a PATIENT joins the call and automatically invalidating app user status.
 *
 * Filters out providers and supervisors, tracking only patients.
 * When a patient joins, automatically invalidates the app user status query to refetch fresh data.
 *
 * Patient identification logic:
 * - local: false (not the one already in the call)
 * - owner: false (not the room owner = not a provider)
 *
 * @param clientId - Healthie client ID to invalidate queries for
 */
export const usePatientJoined = (clientId: string) => {
  const queryClient = useQueryClient();
  const router = useRouter();
  const roomId =
    typeof router.query.roomId === 'string' ? router.query.roomId : '';

  // Listen for participant-joined event
  // Daily.co docs: https://docs.daily.co/reference/daily-js/events/participant-events#participant-joined
  useDailyEvent('participant-joined', (event: DailyEventObjectParticipant) => {
    const participant = event.participant;

    datadogLogs.logger.info(
      'Participant joined',
      callLogContext('patient-join', {
        roomId,
        participantType: 'provider',
        sessionId: participant.session_id,
        userName: participant.user_name,
        isLocal: participant.local,
        isOwner: participant.owner,
      }),
    );

    // CRITICAL CHECK:
    // - local: false = remote participant (not the one already in call)
    // - owner: false = not room owner (owner = provider)
    //
    // If both are false, then a PATIENT joined!
    //
    // Examples:
    // ✅ Patient:      local: false, owner: false  → REFETCH
    // ❌ Provider:     local: true,  owner: true   → NO
    // ❌ Supervisor:   local: false, owner: true   → NO (also owner)
    if (
      !participant.local &&
      !participant.owner &&
      !isDialOutParticipant(participant)
    ) {
      datadogLogs.logger.info(
        'Patient joined the call - invalidating app user status query',
        callLogContext('patient-join', {
          roomId,
          participantType: 'provider',
          sessionId: participant.session_id,
          userName: participant.user_name,
          clientId,
        }),
      );
      // Invalidate app user status query - this will refetch data in all components using it
      queryClient.invalidateQueries({
        queryKey: [QUERY_APP_USER_STATUS_KEY, clientId],
      });
    } else {
      datadogLogs.logger.info(
        'Provider/Supervisor joined - skipping refetch',
        callLogContext('patient-join', {
          roomId,
          participantType: 'provider',
          sessionId: participant.session_id,
          userName: participant.user_name,
          isLocal: participant.local,
          isOwner: participant.owner,
        }),
      );
    }
  });
};
